import { ILocalStorageService } from 'angular-local-storage';
import * as _ from 'lodash';
import { EventRegistry } from 'app/shared/events/event_registry';
import DebugService from 'app/shared/debug.service';
import ArrayDiffer from 'app/shared/utilities/array_differ.service';
import {
    CHART_DISPLAY_TYPE_BAR,
    CHART_DISPLAY_TYPE_LINE,
    ITEM_TYPE_CHART,
} from 'app/overview/dashboard/dashboard_item.service';
import { ImsIconUrlLookupKeyType } from 'app/shared/icons/ims_icon_url_lookup';

// This service:
//  * manages 'panels', separate ui components with local state and access to shared state objects
//  * provides a collection of panel objects (filtered by pane / list of tags) to panel container components
//      - with shared objects (referenced by id) injected into the panel objects
//  * provides a newPanel function that returns an api for interacting with the panel

const log = (...args) => DebugService.log('ImsPanelService', ...args);

export const LOCAL_STORAGE_KEY = 'ImsPanelService:data';
const ERROR_STR = 'ImsPanelService Error:';

/**
 * Lazy creates and holds all of the EventRegistry objects for created panels
 */
class PanelEventRegistryCollection {
    private _eventRegistryByPanelId;

    constructor() {
        this._eventRegistryByPanelId = {};
    }

    getEventRegistryForPanelId(panelId): EventRegistry {
        if (!(panelId in this._eventRegistryByPanelId)) {
            this._eventRegistryByPanelId[panelId] = new EventRegistry();
        }
        return this._eventRegistryByPanelId[panelId];
    }

    deleteEventRegistryForPanelId(panelId): void {
        // make sure to clean up the event registry if it exists
        const event = this.getEventRegistryForPanelId(panelId);
        if (event) {
            event.destroy();
        }
        delete this._eventRegistryByPanelId[panelId];
    }
}

// Gridster specific panel positioning parameters
export interface GridsterParams {
    /**
     * Width of the panel in grid units (when in a dashboard tab)
     */
    gridsterWidth?: number;
    /**
     * Height of the panel in grid units (when in a dashboard tab)
     */
    gridsterHeight?: number;
    /**
     * Column of the panel (when in a dashboard tab)
     */
    gridsterXPos?: number;
    /**
     * Row of the panel (when in a dashboard tab)
     */
    gridsterYPos?: number;
}

export const DETACHED = 'detached';

export interface PanelSpec extends GridsterParams {
    /**
     * The name of the component/directive to be embedded in the panel's body
     */
    innerComponent: string;
    /**
     * The text content of the panel's header (defaults to panelId)
     */
    headerText?: string;
    /**
     * Data object passed to the innerComponent
     * NOTE: passing class instances through panel data is a really bad idea.
     * everything is deep cloned, and we don't want that to happen.
     * instead we can pass a getter function which will always return the same instance
     * regardless of how many times the function itself is cloned.
     */
    innerComponentData?: { [key: string]: any };
    /**
     * Used when checking for existence of panels or defining panel order
     */
    tag?: string;
    /**
     * If present, shows a help btn that opens corresponding help panel
     */
    helpKey?: string;
    /**
     * If present, exits out of closing a panel
     */
    shouldClose?: () => boolean;
    /**
     * Called when the panel is closed, overrides default close behaviour (cannot be used with persistent)
     */
    onClose?: () => void;
    /**
     * A list of groups this panel belongs too, also used for checking the existence of panels
     */
    groups?: Array<string>;
    /**
     * Bootstrap glyphicon (class: 'glyphicon-NAME') used as the panel's icon
     */
    iconName?: ImsIconUrlLookupKeyType;
    /**
     * The type of the icon specified by {@link iconName}
     * Either 'bs' (bootstrap, default) or 'svg'.
     */
    iconType?: 'bs' | 'svg';
    /**
     * Render panel in inverted colours (only used by center panels atm)
     */
    darkTheme?: boolean;
    /**
     * Panel can be collapsed to just its header
     */
    collapsable?: boolean;
    /**
     * Panel starts collapsed
     */
    collapsed?: boolean;
    /**
     * Panel can be resized by dragging the bottom right corner (when detached)
     */
    resizable?: boolean;
    /**
     * Panel can be closed by pressing the 'X' button in the top right
     */
    closable?: boolean;
    /**
     * Panel will become scrollable when panel size is small than the content (when resizable)
     */
    scrollable?: boolean;
    /**
     * Panel can be minimised into the center pane
     */
    dockable?: boolean;

    /**
     * A custom undock implementation should the default not suffice
     */
    onUndock?: (api: PanelApi) => Promise<unknown>;
    /**
     * Panel will be saved to localstorage & rerendered when container is reloaded
     */
    persistent: boolean;
    /**
     * Rendered X position relative to container (when detached)
     */
    detachedXPos?: number;
    /**
     * Rendered Y position relative to container (when detached)
     */
    detachedYPos?: number;
    /**
     * Width of the panel in pixels (when detached)
     */
    detachedWidth?: number;
    /**
     * Height of the panel in pixels (when detached)
     */
    detachedHeight?: number;
    /**
     * Minimum width of the panel in pixels (when detached)
     */
    detachedMinWidth?: number;
    /**
     * Minimum width of the panel in pixels (when detached)
     */
    detachedMinHeight?: number;
    /**
     * Maximum width of the panel in pixels (when detached)
     */
    detachedMaxWidth?: number;
    /**
     * Maximum width of the panel in pixels (when detached)
     */
    detachedMaxHeight?: number;

    /**
     * The UUID of the dashboard tab this panel is a part of
     */
    dashboardTab?: string;

    /**
     * Width of the panel in grid units (when in a dashboard tab)
     */
    gridsterWidth?: number;
    /**
     * Height of the panel in grid units (when in a dashboard tab)
     */
    gridsterHeight?: number;
    /**
     * If present, shows a warning icon in header
     */
    showWarningIcon?: boolean;
    /**
     * Number to display in the panel header (eg. Number of Assets hidden on the map)
     */
    badgeNumber?: number;
    /**
     * Colour for the badge number background, not text.
     */
    badgeColor?: string;
    /**
     * Tooltip text for the badge number.
     */
    badgeTooltip?: string;
    /**
     * Show the badge number.
     */
    showBadge?: boolean;
    /**
     * Show the badge number if the panel is detatched.
     */
    showBadgeOnDetached?: boolean;

    /**
     * (only in the center panel) if the panel is disabled
     */
    disabled?: boolean;

    /**
     * (only in the center panel) if the panel is disabled, the tooltip to display
     */
    disabledTooltip?: string;

    /**
     * (only in the center panel) if the panel is hidden
     */
    hidden?: boolean;
}

export interface PanelApi {
    /**
     * Merge [changes] with the panel's innerComponentData
     * @param changes
     */
    updateData(changes: any): void;

    /**
     * Remove the panel from its current pane and put it in [targetPane]
     * @param {string} targetPane
     */
    moveToPane(targetPane: string): void;

    /**
     * Updates the header text
     * @param {string} text
     */
    updateHeaderText(text: string): void;

    /**
     * Updates showWarningIcon
     * @param {boolean} value
     */
    updateShowWarningIcon(value: boolean): void;

    /**
     * Updates the badge for this panel
     * @param showBadge whether to show the badge or not
     * @param badgeNumber: the number to show inside the badge
     * @param badgeColor: the CSS colors string of the badge
     * @param showBadgeOnDetached: Whether the badge should show in detached mode
     * @param badgeTooltip: The tooltip text to use for the badge hover
     */
    updateBadge(
        showBadge: boolean,
        badgeNumber: number,
        badgeColor: string,
        showBadgeOnDetached: boolean,
        badgeTooltip: string,
    ): void;

    /**
     *
     * @return {string}
     */
    getCurrentPane(): string;

    /**
     *
     * @param {GridsterParams} params
     */
    updateGridsterParams(params: GridsterParams): void;
    /**
     * Move the panel to position [x]/[y]
     * @param {number} x
     * @param {number} y
     */
    moveToPos(x: number, y: number): void;
    /**
     * Resize the panel to [width]/[height]
     * @param {number} width
     * @param {number} height
     */
    resize(width: number, height: number): void;
    /**
     * Collapse the panel to just its header
     */
    collapse(): void;
    /**
     * Uncollapse the panel to full size
     */
    uncollapse(): void;
    /**
     * Render the panel on top of other (detached) panels
     */
    focus(suppressEvent?: boolean): void;
    /**
     * Remove the panel from the app entirely (no undo!)
     * Promise resolves to true if it closes. False otherwise.
     */
    close(): Promise<boolean>;

    /**
     * Sets shouldClose on panel data
     * @param {()=>boolean}
     */
    setShouldClose(fn: () => Promise<boolean>): void;

    /**
     * Creates a new panel (same pane) using this panel's data, overwritten by [spec]
     * @param {PanelSpec} spec
     */
    duplicate(spec: PanelSpec): void;

    /**
     *
     * @param {Function} fn
     * @return {() => void} A function which removes the added listener
     */
    addOnUpdateDataListener(fn: (data: PanelObj) => unknown): () => void;

    /**
     *
     * @param {Function} fn
     * @return {() => void} A function which removes the added listener
     */
    addOnAfterCloseListener(fn: () => void): () => void;

    /**
     * Add a group onto the list of groups for the panel if the
     * group doesn't already exist for the panel
     * @param {string} groupName
     */
    addGroup(groupName: string): void;

    /**
     * Remove a group from the list of groups for the panel if
     * the group already exists in the list
     * @param {string} groupName
     */
    removeGroup(groupName: string): void;

    // if you need to change any properties
    updateSpec(spec: Partial<Omit<PanelSpec, 'groups' | 'innerComponentData'>>): void;

    /**
     *
     */
    id: string;

    /**
     *
     */
    headerText: string;

    /**
     *
     */
    sharedObjectIds: Array<string>;

    /**
     * Detached x/y position (both 0-100)
     */
    position: { x: number; y: number };

    /**
     * Detached width/height (in pixels)
     */
    dimensions: { width: number; height: number };

    /**
     * Grid width/height (in gridster units)
     */
    gridDimensions: { width: number; height: number };

    /**
     * Tag (assigned unique identifier)
     */
    tag: string;

    /**
     * Name of the pane currently rendering the panel
     */
    pane: string;

    /**
     * If present, shows a warning icon in header
     */
    showWarningIcon?: boolean;
}

export interface PanelObj extends PanelSpec {
    api: PanelApi;
    id: string;
}

export class ImsPanelService {
    private readonly _localStorageService: ILocalStorageService;

    // TODO public member because of the tests...
    public readonly _data;

    private readonly _onDestroyListeners;
    private readonly _paneUpdateEventRegistry = new EventRegistry();
    private readonly _panelFocusedEventRegistry = new EventRegistry();
    // used to open new panels in a moving position (so they don't just stack on top of each other)
    private defaultDetachedPanelPos = 0;

    private readonly _panelUpdateDataEventRegistryCollection: PanelEventRegistryCollection;
    private readonly _panelOnAfterCloseEventRegistryCollection: PanelEventRegistryCollection;

    constructor(localStorageService: ILocalStorageService, paneNames: Array<string> = []) {
        this._localStorageService = localStorageService;
        this._panelUpdateDataEventRegistryCollection = new PanelEventRegistryCollection();
        this._panelOnAfterCloseEventRegistryCollection = new PanelEventRegistryCollection();
        this._data = this._localStorageService.get(LOCAL_STORAGE_KEY) || {};

        const dataModel = {
            panelIdsByPane: Object.assign(
                {
                    detached: [],
                    center: [],
                    dashboard: [],
                    infobox: [],
                },
                // any extra panes that you may want to exist
                ...paneNames.map((name) => ({ [name]: [] })),
            ),
            panelTagsByView: {},
            panelObjects: {},
            sharedObjects: {},
            nextPanelObjId: 0,
            nextSharedObjId: 0,
        };

        this._onDestroyListeners = {}; // Stores any panel listeners we need to destroy on close.

        // merge in defaults for keys that don't exist in ls (new keys)
        _.defaultsDeep(this._data, dataModel);

        // strip keys that don't exist in the model (deprecated keys)
        this._data = _.pick(this._data, _.keys(dataModel));

        this._runMigrations();

        this._save();

        log('Initialised data:', this._data);
    }

    _save() {
        const nonPersistedPanels = _.filter(this._data.panelObjects, (panel) => !panel.persistent);

        // if there are any panels that have 'persistent' set to false
        // remove the panel from the data object saved to local storage
        if (nonPersistedPanels.length > 0) {
            const dataCopy = _.cloneDeep(this._data);

            _.forEach(nonPersistedPanels, (panel) => {
                const pane = this._getCurrentPaneOfPanel(panel.id);
                _.remove(dataCopy.panelIdsByPane[pane], (id) => id === panel.id);
                delete dataCopy.panelObjects[panel.id];
            });

            this._localStorageService.set(LOCAL_STORAGE_KEY, dataCopy);
        } else {
            this._localStorageService.set(LOCAL_STORAGE_KEY, this._data);
        }
    }

    private _updatePanesAndSave(panes: Array<string>): void {
        if (!panes) {
            throw new Error(`${ERROR_STR} Panes not provided`);
        }
        log(`********** Panes requiring an update: ${panes} '***********`);
        this._paneUpdateEventRegistry.notifyListeners(panes);
        this._save();
    }

    _nextPanelObjId() {
        return 'panel_' + this._data.nextPanelObjId++;
    }

    _nextSharedObjId() {
        return 'shared_' + this._data.nextSharedObjId++;
    }

    _runMigrations() {
        const self = this;

        // 25/10/2016
        // panels in the center now need 'dockable' set to true
        function migration1(data) {
            const centerPanelTags = [
                'centerSettings',
                'centerAggTable',
                'centerQuicklist',
                'centerMapOptions',
                'centerRfPlanner',
                'centerZoneControls',
            ];

            _.forEach(data.panelObjects, (panel) => {
                if (_.includes(centerPanelTags, panel.tag)) {
                    if (!panel.dockable) {
                        panel.dockable = true;
                    }
                }
            });
        }

        // 05/12/2016
        // updated spec of dashboard item config (currently the only thing stored in sharedObjects)
        function migration2(data) {
            function convertDataFieldTypeToField(fieldType) {
                return (
                    _.get(
                        {
                            signal: 'noise',
                            utilisation: 'avg_tx_bps',
                            rate: 'avg_tx_rate',
                            packets: 'packets1',
                            power: 'input_power',
                            voltage: 'battery_voltage',
                            current: 'battery_current',
                        },
                        fieldType,
                    ) || fieldType
                );
            }

            _.forEach(data.sharedObjects, (obj, id) => {
                if (_.isNil(_.get(obj, 'objType'))) {
                    const newObj = _.cloneDeep(obj);
                    _.assign(newObj, {
                        objType: 'dashboard_item_config',
                        objVersion: 1,
                        group: obj.group ? obj.group.value : null,
                        timeFrame: obj.timeFrame.value,
                        chartDisplayType: obj.chartDisplayType.value,
                        dataField: convertDataFieldTypeToField(obj.dataFieldType.value),
                        dataSource: obj.dataFieldType.type_value,
                    });
                    delete newObj.dataFieldType;
                    data.sharedObjects[id] = newObj;
                }
            });
        }

        // 13/12/2016
        // some dashboard item configs need the following changes:
        //  - filter -> filterSet
        //  - itemSize -> statusItemSize set to itemSize.value
        //  - ignoredStats set to ignoredStats.value
        function migration3(data) {
            _.forEach(data.sharedObjects, (obj, id) => {
                if (
                    _.get(obj, 'objType') === 'dashboard_item_config' &&
                    _.get(obj, 'objVersion') === 1
                ) {
                    const newObj = _.cloneDeep(obj);
                    _.assign(newObj, {
                        objVersion: 2,
                    });
                    if (_.has(newObj, 'filter')) {
                        _.assign(newObj, {
                            filterSet: obj.filter,
                        });
                        delete newObj.filter;
                    }
                    if (_.has(obj, 'ignoredStats.value')) {
                        _.set(newObj, 'ignoredStats', _.get(obj, 'ignoredStats.value'));
                    }
                    if (_.has(obj, 'itemSize.value')) {
                        _.set(newObj, 'statusItemSize', _.get(obj, 'itemSize.value'));
                        delete newObj.itemSize;
                    }
                    data.sharedObjects[id] = newObj;
                }
            });
        }

        // 01/05/2017
        // mapPane and overviewPane have been replaced by a single detached pane
        // renamed centerPane to center
        // caused an issue where center panels were duplicated
        function migration4(data) {
            const ids = data.panelIdsByPane;

            if (_.has(ids, 'mapPane')) {
                ids.detached = [];
                _.forEach(ids.mapPane, (id) => ids.detached.push(id));
                _.forEach(ids.overviewPane, (id) => ids.detached.push(id));
                delete ids.mapPane;
                delete ids.overviewPane;
            }

            if (_.has(ids, 'centerPane')) {
                ids.center = [];
                _.forEach(ids.centerPane, (id) => ids.center.push(id));
                delete ids.centerPane;
            }

            _.forEach(ids, (panelIds, pane) => {
                ids[pane] = _.uniq(panelIds);
            });

            // make sure all the default center pane panels have the correct pane marked
            const centerPanelTags = [
                'centerAggTable',
                'centerQuicklist',
                'centerMapOptions',
                'centerRfPlanner',
                'centerZoneControls',
                'centerHeatMap',
            ];
            _.forEach(centerPanelTags, (tag) => {
                const panelApi = self.getPanelApiByTag(tag);
                if (panelApi && panelApi.getCurrentPane() === undefined) {
                    ids.center.push(panelApi.id);
                }
            });
        }

        // 07/08/2017
        // 'timeFrame' key in dashboard item configs has been deprecated,
        // dashboard items can now specify arbitrary time frames use start and end times
        function migration5(data) {
            _.forEach(data.sharedObjects, (obj, id) => {
                if (
                    _.get(obj, 'objType') === 'dashboard_item_config' &&
                    _.get(obj, 'objVersion') === 2
                ) {
                    const newObj = _.cloneDeep(obj);
                    delete newObj.timeFrame;
                    _.assign(newObj, {
                        objVersion: 3,
                        startTime: 'relative',
                        startTimeRelativeAmount: obj.timeFrame,
                        startTimestamp: null,
                        endTime: 'sync',
                        endTimestamp: null,
                    });
                    data.sharedObjects[id] = newObj;
                }
            });
        }

        // 18/08/2017
        // Introducing legend chart pagination. Set existing line and bar graphs to have
        // a page size of 50
        function migration6(data) {
            _.forEach(data.sharedObjects, (obj, id) => {
                if (
                    _.get(obj, 'objType') === 'dashboard_item_config' &&
                    _.get(obj, 'objVersion') === 3 &&
                    _.get(obj, 'itemType') === ITEM_TYPE_CHART &&
                    [CHART_DISPLAY_TYPE_LINE, CHART_DISPLAY_TYPE_BAR].includes(
                        _.get(obj, 'chartDisplayType'),
                    )
                ) {
                    const newObj = _.cloneDeep(obj);
                    delete newObj.timeFrame;
                    _.assign(newObj, {
                        pageSize: 50,
                    });
                    data.sharedObjects[id] = newObj;
                }
            });
        }

        // 06/09/2017
        // Remove centerSettings and centerAlerts

        function migration7(data) {
            // Remove all panel tags.
            _.forEach(data.panelTagsByView, (tagsByView) => {
                if (tagsByView.center) {
                    _.remove(tagsByView.center, (tagName) => {
                        return tagName === 'centerSettings' || tagName === 'centerAlerts';
                    });
                }
            });
            const panelIdsToRemove = [];
            // Remove panel objects
            _.forEach(data.panelObjects, (panelObject) => {
                if (panelObject.tag == 'centerSettings') {
                    panelIdsToRemove.push(panelObject.id);
                    delete data.panelObjects[panelObject.id];
                    return;
                }
                if (panelObject.tag == 'centerAlerts') {
                    panelIdsToRemove.push(panelObject.id);
                    delete data.panelObjects[panelObject.id];
                    return;
                }
            });

            _.forEach(data.panelIdsByPane, (panePanelIds) => {
                _.remove(panePanelIds, (id) => {
                    return (
                        _.remove(panelIdsToRemove, (check) => {
                            return check === id;
                        }).length > 0
                    );
                });
            });
        }

        migration1(this._data);
        migration2(this._data);
        migration3(this._data);
        migration4(this._data);
        migration5(this._data);
        migration6(this._data);
        migration7(this._data);
    }

    private _getCurrentPaneOfPanel(panelId: string): string {
        if (!panelId) {
            throw new Error(`${ERROR_STR} Panel id not provided`);
        }

        return _.reduce(
            this._data.panelIdsByPane,
            (accum, panelIdsInPane, pane) => {
                return _.includes(panelIdsInPane, panelId) ? pane : accum;
            },
            null,
        );
    }

    _removePanelFromPane(panelId, pane) {
        if (!panelId) {
            throw new Error(`${ERROR_STR} Panel id not provided`);
        }
        if (!pane) {
            throw new Error(`${ERROR_STR} Pane not provided`);
        }

        _.remove(this._data.panelIdsByPane[pane], (id) => {
            return id === panelId;
        });
    }

    _addPanelToPane(panelId, pane) {
        if (!panelId) {
            throw new Error(`${ERROR_STR} Panel id not provided`);
        }
        if (!pane) {
            throw new Error(`${ERROR_STR} Pane not provided`);
        }

        const panelIds = this._data.panelIdsByPane[pane];

        if (!_.includes(panelIds, panelId)) {
            panelIds.push(panelId);
        }
    }

    // Removes the panel from its current pane and deletes the panel object entirely
    _deletePanel(panelId) {
        if (!panelId) {
            throw new Error(`${ERROR_STR} Panel id not provided`);
        }

        // Clean up all our event registries for this panel
        this._panelUpdateDataEventRegistryCollection.deleteEventRegistryForPanelId(panelId);
        this._panelOnAfterCloseEventRegistryCollection.deleteEventRegistryForPanelId(panelId);

        const paneId = this._getCurrentPaneOfPanel(panelId);

        // Only remove panel if the pane exists
        if (paneId) {
            this._removePanelFromPane(panelId, paneId);
        }

        delete this._data.panelObjects[panelId];
    }

    // Returns true if [panel] contains every key/value pair in [filter]
    _panelMatchesFilter(panel, filter) {
        if (!panel) {
            throw new Error(`${ERROR_STR} Panel not provided`);
        }
        if (!filter) {
            throw new Error(`${ERROR_STR} Filter not provided`);
        }

        return _.every(_.toPairs(filter), (prop) => {
            return _.get(panel, prop[0]) === prop[1];
        });
    }

    // Return an array of panelIds that are dependant on [sharedObjId]
    private _dependantPanelIds(sharedObjId: string): Array<string> {
        return _.filter(this._data.panelObjects, (panel) => {
            return _.some(panel.injectSharedObjects, (id) => id === sharedObjId);
        }).map((panel) => panel.id);
    }

    // Returns an array of (unique) panes that include a panel in [panelIds]
    _panesByPanelIds(panelIds: Array<string>) {
        return _.uniq(panelIds.map((id) => this._getCurrentPaneOfPanel(id)));
    }

    refreshPanels() {
        // Use an empty object if the panel objects has not yet been initialised
        Object.keys(this._data.panelObjects || {}).forEach((panelId) => {
            const pane = this._getCurrentPaneOfPanel(panelId);
            this._updatePanesAndSave([pane]);
        });
    }

    // Returns an api object for interacting with a panel (see this.newPanel for details)
    _panelApi(panelId: string): PanelApi {
        if (!panelId) {
            throw `${ERROR_STR} Panel id not provided`;
        }

        const panel = this._data.panelObjects[panelId];
        const pane = this._getCurrentPaneOfPanel(panelId);

        const onUpdateDataEventRegistry =
            this._panelUpdateDataEventRegistryCollection.getEventRegistryForPanelId(panelId);
        const onAfterCloseEventRegistry =
            this._panelOnAfterCloseEventRegistryCollection.getEventRegistryForPanelId(panelId);

        const simpleUpdate = (changes) => {
            log('updating', panelId, changes);
            _.merge(panel, changes);
            this._updatePanesAndSave([pane]);
            const updatedObject = this._injectedPanelObj(panel.id);
            if (updatedObject) {
                onUpdateDataEventRegistry.notifyListeners(updatedObject);
            }
        };

        const updateData = (changes) => {
            let numberOfChangesMade = 0;
            const changedSharedObjIds = [];

            _.forEach(_.keys(changes), (key) => {
                const newValue = changes[key];

                // check if the key is part of a shared object
                if (_.includes(_.keys(panel.injectSharedObjects), key)) {
                    const sharedObjectId = panel.injectSharedObjects[key];
                    this._data.sharedObjects[sharedObjectId] = newValue;
                    changedSharedObjIds.push(sharedObjectId);
                    ++numberOfChangesMade;
                } else if (panel.innerComponentData[key] !== newValue) {
                    panel.innerComponentData[key] = newValue;
                    ++numberOfChangesMade;
                }
            });

            if (numberOfChangesMade > 0) {
                const dependantPanelIds = changedSharedObjIds.reduce(
                    (accum, sharedObjId) => {
                        //TODO: duplicates are uniq-ed out later (by __panesByPanelIds) but this should be a set instead
                        return accum.concat(this._dependantPanelIds(sharedObjId));
                    },
                    [panelId],
                );
                this._updatePanesAndSave(this._panesByPanelIds(dependantPanelIds));
                onUpdateDataEventRegistry.notifyListeners(this._injectedPanelObj(panel.id));
            }
        };

        const addOnUpdateDataListener = (callback) =>
            onUpdateDataEventRegistry.addEventListener(callback);

        const moveToPane = (targetPane) => {
            this._removePanelFromPane(panelId, pane);
            this._addPanelToPane(panelId, targetPane);
            this._updatePanesAndSave([pane, targetPane]);
        };

        const focus = (suppressEvent = false) => {
            const pane = this._getCurrentPaneOfPanel(panelId);
            const paneIds = this._data.panelIdsByPane[pane];
            const paneIdsWithIdRemoved = _.without(paneIds, panelId);

            this._data.panelIdsByPane[pane] = _.concat(paneIdsWithIdRemoved, panelId);

            if (!suppressEvent) {
                this._panelFocusedEventRegistry.notifyListeners(panelId);
            }

            if (!_.isEqual(paneIds, this._data.panelIdsByPane[pane])) {
                this._updatePanesAndSave([pane]);
            }
        };

        const addGroup = (groupName: string) => {
            if (!panel.groups.includes(groupName)) {
                panel.groups.push(groupName);
                this._updatePanesAndSave([pane]);
            }
        };

        const removeGroup = (groupName: string) => {
            if (panel.groups.includes(groupName)) {
                panel.groups = panel.groups.filter((g) => g !== groupName);
                this._updatePanesAndSave([pane]);
            }
        };

        const duplicate = (spec) => {
            const newPanelSpec = _.merge(_.cloneDeep(panel), spec);
            const newPanelApi = this.newPanel(pane, newPanelSpec);
            this._addPanelToPane(newPanelApi.id, pane);
            this._updatePanesAndSave([pane]);
            return newPanelApi;
        };

        const addOnAfterCloseListener = (fn: () => void) => {
            return onAfterCloseEventRegistry.addEventListener(fn);
        };

        const setShouldClose = (fn: () => Promise<boolean>) => {
            panel.shouldClose = fn;
        };

        const close = async (): Promise<boolean> => {
            if (panel.shouldClose) {
                const shouldClose = await panel.shouldClose();
                if (!shouldClose) {
                    return false;
                }
            }

            if (panel.onClose) {
                // Wrap the result in a promise and wait for it...
                // If it's a promise it works as normal.
                // If it's not a promise, resolve immediately.
                const onCloseResult = panel.onClose();
                await Promise.resolve(onCloseResult);
            } else {
                await this.closePanel(panelId);
            }
            return true;
        };

        return {
            id: panelId,
            headerText: panel.headerText,
            showWarningIcon: panel.showWarningIcon,
            getCurrentPane: () => pane,
            tag: panel.tag,
            sharedObjectIds: panel.injectSharedObjects,
            position: {
                x: panel.detachedXPos,
                y: panel.detachedYPos,
            },
            dimensions: {
                width: panel.detachedWidth,
                height: panel.detachedHeight,
            },
            gridDimensions: {
                width: panel.gridsterWidth,
                height: panel.gridsterHeight,
            },
            pane,
            updateHeaderText: (text) => {
                if (text !== panel.headerText) {
                    simpleUpdate({
                        headerText: text,
                    });
                }
            },
            updateShowWarningIcon: (value) => {
                if (value !== panel.showWarningIcon) {
                    simpleUpdate({
                        showWarningIcon: value,
                    });
                }
            },
            moveToPos: (x, y) =>
                simpleUpdate({
                    detachedXPos: x,
                    detachedYPos: y,
                }),
            resize: (w, h) =>
                simpleUpdate({
                    detachedWidth: w,
                    detachedHeight: h,
                }),
            updateGridsterParams: (params: GridsterParams) => simpleUpdate(params),
            collapse: () =>
                simpleUpdate({
                    collapsed: true,
                }),
            uncollapse: () =>
                simpleUpdate({
                    collapsed: false,
                }),
            updateBadge: (
                showBadge,
                badgeNumber,
                badgeColor,
                showBadgeOnDetached,
                badgeTooltip,
            ) => {
                simpleUpdate({
                    showBadge,
                    badgeNumber,
                    badgeColor,
                    showBadgeOnDetached,
                    badgeTooltip,
                });
            },
            updateSpec: (spec: Partial<Omit<PanelSpec, 'groups' | 'innerComponentData'>>) => {
                simpleUpdate(spec);
            },
            addOnUpdateDataListener,
            addGroup,
            removeGroup,
            updateData,
            moveToPane,
            focus,
            duplicate,
            close,
            addOnAfterCloseListener,
            setShouldClose,
        };
    }

    // Returns a cloned panel object with any shared data objects injected
    _injectedPanelObj(panelId: string): PanelObj {
        if (!panelId) {
            throw new Error(`${ERROR_STR} Panel id not provided`);
        }

        const panel = _.cloneDeep(this._data.panelObjects[panelId]);

        // It seems sometimes this can happen?? In the worse case this can crash cesium
        if (!panel) {
            console.warn(`No panel for id ${panelId}`);
            return null;
        }

        const injections = panel.injectSharedObjects;

        panel.api = this._panelApi(panelId);

        if (injections) {
            // inject any requested shared objects into the panel's innerComponentData
            _.forEach(_.keys(injections), (propToInject) => {
                const sharedObjId = injections[propToInject];
                panel.innerComponentData[propToInject] = this._data.sharedObjects[sharedObjId];
            });

            delete panel.injectSharedObjects;
        }
        return panel;
    }

    ////////////////////
    // PUBLIC METHODS //
    ////////////////////

    // Calls [listenerFunc] with an array of panes that need to be updated
    public addPaneUpdateListener(listenerFunc: (panes: Array<string>) => unknown): () => void {
        return this._paneUpdateEventRegistry.addEventListener(listenerFunc);
    }

    getPanelApiById(panelId) {
        const panel = _.find(this._data.panelObjects, (panel) => panel.id === panelId);
        return panel !== undefined ? this._panelApi(panel.id) : undefined;
    }

    // Returns an array of the panels objects that are members of [group]
    getPanelsByGroup(group) {
        if (!group) {
            throw new Error(`${ERROR_STR} Group not provided`);
        }

        const panels = _.filter(this._data.panelObjects, (panel) =>
            _.includes(panel.groups, group),
        );

        return _.map(panels, (panel) => this._injectedPanelObj(panel.id));
    }

    getMostRecentlyFocusedDetachedPanelOfGroup(group: string) {
        const lastPanelId = _.last(
            _.filter(this._data.panelIdsByPane.detached, (id) =>
                _.includes(this._data.panelObjects[id].groups, group),
            ),
        );
        return this._data.panelObjects[lastPanelId];
    }

    // Returns the api for the first panel found matching [tag]
    getPanelApiByTag(tag): PanelApi {
        if (!tag) {
            throw new Error(`${ERROR_STR} Tag not provided`);
        }

        const panel = _.find(this._data.panelObjects, (panel) => panel.tag === tag);

        return panel !== undefined ? this._panelApi(panel.id) : undefined;
    }

    // Returns the first panel object found matching [tag]
    getPanelByTag(tag: string): PanelObj | undefined {
        if (!tag) {
            throw new Error(`${ERROR_STR} Tag not provided`);
        }

        const panel = _.find(this._data.panelObjects, (panel) => panel.tag === tag);
        return panel ? this._injectedPanelObj(panel.id) : undefined;
    }

    getPanelById(id: string): PanelObj {
        const panel = _.find(this._data.panelObjects, (panel) => panel.id === id);
        return panel ? this._injectedPanelObj(panel.id) : undefined;
    }

    // Returns an object containing:
    //     panelsMap   <obj>    keys: panel ids, values: panel objects - for all panels in [pane]
    //     orderedIds  <array>  panel ids of all panels in [pane] (ordered/filtered by the tags in [view])
    getPanelsByPane(
        pane: string,
        view?,
    ): {
        panelsMap: Record<string, PanelObj>;
        orderedIds: Array<string>;
    } {
        if (!pane) {
            throw new Error(`${ERROR_STR} Pane id not provided`);
        }

        const panelIds = this._data.panelIdsByPane[pane];
        const panels = _.map(panelIds, (id) => this._injectedPanelObj(id));
        const panelsMap = _.keyBy(panels, 'id');

        if (view) {
            const tagOrder = this.getViewTags(view, pane);

            const idsFromTags = _.map(tagOrder, (tag) => {
                const panel = _.find(panels, (panel) => panel.tag === tag);
                return panel ? panel.id : null;
            });

            return {
                panelsMap,
                orderedIds: _.filter(idsFromTags),
            };
        } else {
            return {
                panelsMap,
                orderedIds: panelIds,
            };
        }
    }

    // Returns an object containing:
    //     panelsMap   <obj>    keys: panel id, values: panel obj - for each tag in [tags] that matched a panel
    //     orderedIds  <array>  panel id for each tag in [tags] that matched a panel, maintains provided order
    getPanelsByTags(tags) {
        if (!tags) {
            throw new Error(`${ERROR_STR} Tags not provided`);
        }

        const panels = _.filter(_.map(tags, (tag) => this.getPanelByTag(tag)));

        return {
            orderedIds: _.map(panels, (panel) => panel.id),
            panelsMap: _.keyBy(panels, 'id'),
        };
    }

    /**
     * Create a new panel in [pane] with default properties overwritten by [spec]
     * WARNING: when creating panel inner components keep in mind that each time any panel is changed,
     * all of the panels in the same pane as that panel will receive a NEW panelData and panelApi object.
     * i.e. do not put computationally intensive operations in innerComponent $onChanges unless you manually check
     * to see if panelData has actually changed values (you can't rely on just a reference check)
     * @param {string} pane The pane to render this panel in
     * @param {PanelSpec} spec The spec for the new panel
     * @return {PanelApi} The api for this panel
     */
    newPanel(pane: string, spec: PanelSpec): PanelApi {
        if (!pane) {
            throw new Error(`${ERROR_STR} Pane not provided`);
        }

        this.defaultDetachedPanelPos =
            this.defaultDetachedPanelPos > 20 ? 1 : this.defaultDetachedPanelPos + 2;

        const panelId = this._nextPanelObjId();

        const defaultProperties = {
            headerText: panelId,
            showWarningIcon: false,
            groups: [],
            innerComponentData: {},
            collapsable: true,
            resizable: true,
            closable: true,
            scrollable: false,
            // Default persistent to null. New panels must set this or attempting to open the panel will throw an error
            persistent: null,
            detachedXPos: this.defaultDetachedPanelPos,
            detachedYPos: this.defaultDetachedPanelPos,
            gridsterWidth: 2,
            gridsterHeight: 6,
        };

        const panel = _.defaults(
            {
                id: panelId,
            },
            spec,
            defaultProperties,
        );

        if (panel.persistent === null) {
            throw new Error('Attempting to open a panel without setting persistent. NOT ALLOWED!');
        }

        if (panel.onClose && panel.persistent) {
            throw new Error(
                `${ERROR_STR} Can not provide an onClose function to a persisted panel`,
            );
        }

        this._addPanelToPane(panelId, pane);
        this._data.panelObjects[panelId] = panel;
        this._updatePanesAndSave([pane]);
        const panelApi = this._panelApi(panelId);
        setTimeout(() => {
            // if we don't wait, the click that created the panel will steal focus from the new panel
            panelApi.focus();
        }, 0);
        return panelApi;
    }

    /**
     * Checks to see if a panel already exists with the provided tag.
     * If no panel exists, it's opened. If a panel already exists with the same tag, it's focused.
     * @return {boolean} True if the panel was created, false if an existing panel was focused
     */
    newPanelOrFocus(pane, spec: PanelSpec) {
        const tag = spec.tag;
        if (!tag) {
            throw new Error(`can't call newPanelOrFocus without a tag in the panel spec!`);
        }
        const panel = this.getPanelByTag(tag);
        if (panel) {
            panel.api.focus();
            return false;
        } else {
            this.newPanel(pane, spec);
            return true;
        }
    }

    async closeAllPanelsOfType(panelType: string): Promise<void> {
        this._data.panelIdsByPane[panelType].forEach((id) => this.closePanel(id));
    }

    async closePanel(panelId: string): Promise<void> {
        const pane = this._getCurrentPaneOfPanel(panelId);

        // notify all our on close listeners!
        const onAfterCloseEventRegistry =
            this._panelOnAfterCloseEventRegistryCollection.getEventRegistryForPanelId(panelId);
        await onAfterCloseEventRegistry.notifyListeners();

        // actually delete the panel from memory
        this._deletePanel(panelId);
        this._updatePanesAndSave([pane]);
    }

    // Update multiple dashboard item panels at once
    dashboardBulkUpdate(changeSet) {
        _.forEach(changeSet, (change) => {
            // Explicitly update each panel API object
            const panelApi = this.getPanelApiById(change.panelId);
            panelApi.updateGridsterParams(change.changes as GridsterParams);
        });
        this._updatePanesAndSave(['dashboard']);
    }

    // Returns an array of tag strings representing the order of tagged panels in [pane]
    public getViewTags(view: string, pane: string): Array<string> {
        return _.get(this._data.panelTagsByView, [view, pane]);
    }

    // Adds [newTags] to [pane], keeps the existing order of any tags already present
    setViewTags(view, pane, newTags) {
        const currentTags = this.getViewTags(view, pane);
        const diff = ArrayDiffer.findChanges(currentTags, newTags, (t) => t);

        if (!diff.noChanges) {
            _.set(
                this._data.panelTagsByView,
                [view, pane],
                !currentTags
                    ? newTags
                    : currentTags.filter((tag) => !diff.removed.includes(tag)).concat(diff.added),
            );
            this._updatePanesAndSave([pane]);
        }
    }

    // Reorder tagged panels in [pane]
    // You do not need to pass all of the tags in [newTagOrder], can be a subset (see tests)
    reorderViewTags(view, pane, newTagOrder) {
        const currentTags = this.getViewTags(view, pane);
        const remainingTags = _.difference(currentTags, newTagOrder);

        const newTags = _.reduce(
            remainingTags,
            (order, tag) => {
                const index = _.indexOf(currentTags, tag);
                order.splice(index, 0, tag);
                return order;
            },
            newTagOrder,
        );

        _.set(this._data.panelTagsByView, [view, pane], newTags);
        this._updatePanesAndSave([pane]);
    }

    // Returns an array of panel APIs in [pane] that contain all of the key/value pairs in [filter]
    getPanelsApisInPaneMatchingFilter(pane, filter) {
        const ids = _.filter(this._data.panelIdsByPane[pane], (panelId) => {
            const panel = _.get(this._data.panelObjects, panelId);
            return this._panelMatchesFilter(panel, filter);
        });
        return _.map(ids, (id) => this._panelApi(id));
    }

    // Returns an array of panel APIs in [pane] that contain all of the key/value pairs in [filter]
    getPanelsInPaneMatchingFilter(pane, filter) {
        const ids = _.filter(this._data.panelIdsByPane[pane], (panelId) => {
            const panel = _.get(this._data.panelObjects, panelId);
            return this._panelMatchesFilter(panel, filter);
        });
        return _.map(ids, (id) => this._injectedPanelObj(id));
    }

    // Returns the number of panels in [pane] that contain all of the key/value pairs in [filter]
    numberOfPanelsInPaneMatchingFilter(pane, filter) {
        return _.reduce(
            _.get(this._data.panelIdsByPane, pane),
            (numMatchedPanels, panelId) => {
                const panel = _.get(this._data.panelObjects, panelId);
                return this._panelMatchesFilter(panel, filter)
                    ? ++numMatchedPanels
                    : numMatchedPanels;
            },
            0,
        );
    }

    // Registers a new shared object and returns its generated id
    // The only required key is 'objType'
    registerSharedObj(obj) {
        if (!obj) {
            throw new Error(`${ERROR_STR} Object not provided`);
        }

        if (_.isNil(_.get(obj, 'objType'))) {
            throw new Error(`${ERROR_STR} Required key 'objType' not present`);
        }

        const id = this._nextSharedObjId();

        this._data.sharedObjects[id] = obj;

        this._save();

        return id;
    }

    // Returns the shared object with [id]
    getSharedObjById(id) {
        if (!id) {
            throw new Error(`${ERROR_STR} Shared object id not provided`);
        }
        return _.cloneDeep(this._data.sharedObjects[id]);
    }

    // Deletes the shared obj [id] and, if [closeDependants], any panels using the shared obj
    async removeSharedObj(id, closeDependants = false): Promise<void> {
        if (!id) {
            throw new Error(`${ERROR_STR} Shared object id not provided`);
        }

        if (_.has(this._data.sharedObjects, id)) {
            const dependantPanels = this._dependantPanelIds(id);
            const affectedPanes = this._panesByPanelIds(dependantPanels);

            delete this._data.sharedObjects[id];

            if (closeDependants) {
                for (const panelId of dependantPanels) {
                    await this.closePanel(panelId);
                }
            }

            this._updatePanesAndSave(affectedPanes);
        }
    }

    // Returns the number of panel objects stored, only used for testing a  tm
    numberOfPanels() {
        return _.size(this._data.panelObjects);
    }

    // Call callback whenever a panel becomes focused, passing the focused panel's id as param
    listenForFocus(callback) {
        return this._panelFocusedEventRegistry.addEventListener(callback);
    }
}
